<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { $api } from '@/services';
import cloudIcon from '@/assets/images/icons/cloud-upload-fill.png';
import trashIcon from '@/assets/images/icons/trash-fill-blue.png';

const props = defineProps({
	info: { type: Object, required: true },
	studentId: { type: [String, Number], required: true },
	screeningId: { type: [String, Number], required: true },
	saving: { type: Boolean, default: false },
	saved: { type: Boolean, default: false },
	editing: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'submit']);
const store = useStore();

const tab = ref('content');
const title = ref('');
const date = ref('');
const comment = ref('');
const showBands = ref(true);
const files = ref([]);
const fileError = ref('');
const uploading = ref(false);
const deletingId = ref(null);
const showSuccess = ref(false);
const fileInput = ref(null);
const FILE_PREFIX = 'screening_scale_file';

const todayValue = () => {
	const now = new Date();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	const day = String(now.getDate()).padStart(2, '0');
	return `${now.getFullYear()}-${month}-${day}`;
};

watch(
	() => props.info,
	(info) => {
		title.value = info?.title || '';
		date.value = todayValue();
		comment.value = info?.comment || '';
	},
	{ immediate: true }
);

const conclusionText = computed(() => {
	const score = props.info?.totalScore;
	const conclusion = props.info?.conclusion;
	if (score !== null && score !== undefined && score !== '' && conclusion) {
		return `${score} điểm - ${conclusion}`;
	}
	if (conclusion) return conclusion;
	if (score !== null && score !== undefined && score !== '') return `${score} điểm`;
	return '';
});

const bandItems = computed(() =>
	Array.isArray(props.info?.bands) ? props.info.bands : []
);

const bandText = computed(() =>
	typeof props.info?.bands === 'string' ? props.info.bands : ''
);

const hasBands = computed(() => bandItems.value.length || bandText.value);

const unwrapFiles = (body) => {
	if (Array.isArray(body)) return body;
	if (Array.isArray(body?.data)) return body.data;
	if (Array.isArray(body?.files)) return body.files;
	return [];
};

const fileName = (file) =>
	file?.name || file?.filename || file?.file_name || 'Tài liệu';

const loadFiles = async () => {
	try {
		const res = await $api.screening.listFiles(
			props.studentId,
			props.screeningId
		);
		files.value = unwrapFiles(res?.data);
	} catch (err) {
		files.value = [];
	}
};

const alertError = async (err) => {
	const message =
		err?.response?.data?.message ||
		err?.response?.data?.error ||
		err?.message ||
		'Đã có lỗi xảy ra. Vui lòng thử lại';
	await store.dispatch('setAlertMessage', { message, type: 'danger' });
};

const uploadOne = async (file) => {
	const {
		data: { data },
	} = await $api.file.getSignatures({
		mime_types: [file.type || 'application/octet-stream'],
		prefix: FILE_PREFIX,
	});
	const signatureData = data?.[0];
	if (!signatureData) throw new Error('Không lấy được chữ ký tải tệp');
	const formData = new FormData();
	formData.append('AWSAccessKeyId', signatureData.AWSAccessKeyId || '');
	formData.append('key', signatureData.key || '');
	formData.append('acl', signatureData.acl || '');
	formData.append(
		'Content-Type',
		signatureData['Content-Type'] || file.type || ''
	);
	formData.append('x-amz-signature', signatureData.signature || '');
	formData.append('Signature', signatureData.signature || '');
	formData.append('Policy', signatureData.policy || '');
	formData.append(
		'success_action_status',
		signatureData.success_action_status || ''
	);
	formData.append('file', file);
	const uploaded = await $api.file.uploadToS3(signatureData.action, formData);
	const keyMatch = String(uploaded?.data || '').match(/<Key>(.*)<\/Key>/);
	const uploadedKey = keyMatch?.[1] || signatureData.key || '';
	const assetKey = uploadedKey.split('/').pop();
	const signature = signatureData.signature;
	await $api.screening.uploadFile(props.studentId, props.screeningId, {
		asset_key: assetKey,
		mime_type:
			file.type ||
			signatureData['Content-Type'] ||
			'application/octet-stream',
		name: file.name,
		signature,
	});
};

const onFiles = async (event) => {
	fileError.value = '';
	const picked = Array.from(event.target.files || []);
	event.target.value = '';
	const allowed = ['png', 'jpg', 'jpeg', 'pdf', 'docx', 'xlsx', 'xls'];
	const next = [];
	picked.forEach((file) => {
		const ext = file.name.split('.').pop().toLowerCase();
		if (!allowed.includes(ext)) {
			fileError.value = 'Định dạng tệp không được hỗ trợ';
			return;
		}
		next.push(file);
	});
	const room = 10 - files.value.length;
	if (room <= 0) {
		fileError.value = 'Tối đa 10 tài liệu/ học sinh';
		return;
	}
	const batch = next.slice(0, room);
	if (next.length > room) fileError.value = 'Tối đa 10 tài liệu/ học sinh';
	if (!batch.length) return;
	uploading.value = true;
	try {
		for (const file of batch) {
			await uploadOne(file);
		}
		await loadFiles();
	} catch (err) {
		await alertError(err);
	} finally {
		uploading.value = false;
	}
};

const removeFile = async (file) => {
	if (!file?.id || deletingId.value) return;
	deletingId.value = file.id;
	try {
		await $api.screening.deleteFile(
			props.studentId,
			props.screeningId,
			file.id
		);
		files.value = files.value.filter((item) => item.id !== file.id);
	} catch (err) {
		await alertError(err);
	} finally {
		deletingId.value = null;
	}
};

const openPicker = () => {
	if (uploading.value || files.value.length >= 10) {
		if (files.value.length >= 10) {
			fileError.value = 'Tối đa 10 tài liệu/ học sinh';
		}
		return;
	}
	fileInput.value?.click();
};

const canContinue = computed(() => title.value.trim() && date.value);

const continueNext = () => {
	if (props.saving || !canContinue.value) return;
	if (props.saved) {
		tab.value = 'files';
		showSuccess.value = true;
		return;
	}
	emit('submit', {
		title: title.value.trim(),
		date: date.value,
		comment: comment.value,
	});
};

watch(
	() => props.saved,
	(saved) => {
		if (!saved) return;
		tab.value = 'files';
		showSuccess.value = true;
	}
);

watch(
	() => [props.studentId, props.screeningId],
	() => {
		loadFiles();
	},
	{ immediate: true }
);
</script>

<template>
	<div class="fixed inset-0 z-50 flex justify-end">
		<div class="absolute inset-0 bg-black bg-opacity-20" @click="emit('close')"></div>
		<aside class="screening-result-form">
			<div class="screening-result-form__header">
				<div class="flex items-center text-2xl font-bold text-neutral-700">
					<svg class="mr-2" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M3 3C3 1.34315 4.34315 0 6 0H13.9393C14.3372 0 14.7187 0.158035 15 0.43934L20.5607 6C20.842 6.28131 21 6.66284 21 7.06066V21C21 22.6569 19.6569 24 18 24H6C4.34315 24 3 22.6569 3 21V3ZM14.25 5.25V2.25L18.75 6.75H15.75C14.9216 6.75 14.25 6.07843 14.25 5.25ZM12.75 10.5C12.75 10.0858 12.4142 9.75 12 9.75C11.5858 9.75 11.25 10.0858 11.25 10.5V12.75H9C8.58579 12.75 8.25 13.0858 8.25 13.5C8.25 13.9142 8.58579 14.25 9 14.25H11.25V16.5C11.25 16.9142 11.5858 17.25 12 17.25C12.4142 17.25 12.75 16.9142 12.75 16.5V14.25H15C15.4142 14.25 15.75 13.9142 15.75 13.5C15.75 13.0858 15.4142 12.75 15 12.75H12.75V10.5Z"
							fill="#94A3B8"
						/>
					</svg>
					{{ props.editing ? 'Chỉnh sửa' : 'Tạo mới' }}
				</div>
				<button type="button" class="text-neutral-500" @click="emit('close')">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path
							d="M6 6l12 12M18 6L6 18"
							stroke="currentColor"
							stroke-width="1.8"
							stroke-linecap="round"
						/>
					</svg>
				</button>
			</div>
			<div class="screening-result-form__tabs">
				<button
					type="button"
					:class="{ 'is-active': tab === 'content' }"
					@click="tab = 'content'"
				>
					Nội dung
				</button>
				<button
					type="button"
					:class="{ 'is-active': tab === 'files' }"
					@click="tab = 'files'"
				>
					Tài liệu
				</button>
			</div>
			<div class="flex-1 overflow-y-auto px-6 py-6">
				<div v-if="tab === 'content'">
					<p class="text-neutral-600 mb-6">
						Bạn hãy điền các thông tin dưới đây để tạo kết quả sàng lọc
					</p>
					<label class="screening-field">
						<span>Tiêu đề *</span>
						<input v-model="title" type="text" maxlength="100" />
						<em>{{ title.length }}/100 ký tự</em>
					</label>
					<label class="screening-field">
						<span>Ngày thực hiện *</span>
						<input v-model="date" type="date" readonly />
					</label>
					<label class="screening-field">
						<span>Kết luận</span>
						<input :value="conclusionText" type="text" readonly />
					</label>
					<button
						v-if="hasBands && !showBands"
						type="button"
						class="screening-score-link"
						@click="showBands = true"
					>
						Cách tính điểm?
					</button>
					<div v-if="hasBands && showBands" class="screening-bands">
						<ul v-if="bandItems.length">
							<li v-for="(band, index) in bandItems" :key="index">
								Tổng điểm từ {{ band.range }}: {{ band.label }}
							</li>
						</ul>
						<p v-else>{{ bandText }}</p>
						<button
							type="button"
							class="screening-bands__close"
							aria-label="Đóng"
							@click="showBands = false"
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
								<path
									d="M4 4l8 8M12 4L4 12"
									stroke="currentColor"
									stroke-width="1.5"
									stroke-linecap="round"
								/>
							</svg>
						</button>
					</div>
					<label class="screening-field">
						<span>Nhận xét</span>
						<textarea v-model="comment" rows="4"></textarea>
					</label>
				</div>
				<div v-else class="screening-docs">
					<input
						ref="fileInput"
						class="screening-docs__input"
						type="file"
						multiple
						accept=".png,.jpg,.jpeg,.pdf,.docx,.xlsx,.xls"
						@change="onFiles"
					/>
					<template v-if="!files.length">
						<p class="screening-docs__hint">
							Bạn hãy tải lên các tài liệu về kết quả thăm khám của trẻ (nếu có)
						</p>
						<button
							type="button"
							class="screening-drop"
							:disabled="uploading"
							@click="openPicker"
						>
							<img :src="cloudIcon" alt="" width="48" height="48" />
							<span>Tải lên tài liệu đánh giá</span>
							<small>
								PNG, JPG, PDF, DOCX, XLXS
								<br />
								<br />
								Tối đa 10 tài liệu/ học sinh
							</small>
						</button>
					</template>
					<template v-else>
						<ul class="screening-files">
							<li v-for="(file, index) in files" :key="file.id || index">
								<span>{{ index + 1 }}. {{ fileName(file) }}</span>
								<button
									type="button"
									:disabled="deletingId === file.id"
									@click="removeFile(file)"
								>
									<img :src="trashIcon" alt="Xóa" width="16" height="16" />
								</button>
							</li>
						</ul>
						<button
							type="button"
							class="screening-upload-link"
							:disabled="uploading || files.length >= 10"
							@click="openPicker"
						>
							<img :src="cloudIcon" alt="" width="24" height="24" />
							Tải lên tài liệu đánh giá
						</button>
					</template>
					<p v-if="uploading" class="screening-docs__status">Đang tải lên...</p>
					<p v-if="fileError" class="screening-file-error">{{ fileError }}</p>
				</div>
			</div>
			<div v-if="tab === 'content'" class="border-t screening-result-form__footer">
				<button
					type="button"
					class="button-p w-fit px-5"
					:disabled="!canContinue || saving"
					@click="continueNext"
				>
					{{ saving ? 'Đang lưu...' : 'Tiếp tục' }}
				</button>
			</div>
			<div v-if="showSuccess && tab === 'files'" class="screening-success">
				<div>
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
						<circle cx="10" cy="10" r="9" stroke="#2AA797" stroke-width="1.5" />
						<path
							d="M6 10.2l2.4 2.4L14 7.4"
							stroke="#2AA797"
							stroke-width="1.6"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
					<p>Tạo kết quả sàng lọc thành công</p>
				</div>
				<button type="button" @click="showSuccess = false">
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
						<path
							d="M5 5l10 10M15 5L5 15"
							stroke="#2AA797"
							stroke-width="1.6"
							stroke-linecap="round"
						/>
					</svg>
				</button>
			</div>
		</aside>
	</div>
</template>

<style lang="scss" scoped>
.screening-result-form {
	position: relative;
	display: flex;
	flex-direction: column;
	height: 100%;
	width: 100%;
	max-width: 512px;
	background: #fff;
	box-shadow: 0 20px 24px rgba(20, 37, 63, 0.06);
}

.screening-result-form__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 24px;
	background: #eef3f8;
}

.screening-result-form__tabs {
	display: flex;
	gap: 24px;
	padding: 0 24px;
	border-bottom: 1px solid #e1e7ef;

	button {
		padding: 16px 0;
		font-size: 14px;
		font-weight: 600;
		color: #94a3b8;
		border-bottom: 2px solid transparent;
		margin-bottom: -1px;
	}

	.is-active {
		color: #d89210;
		border-bottom-color: #f0bb4f;
	}
}

.screening-result-form__footer {
	display: flex;
	justify-content: flex-end;
	padding: 16px;
	border-color: #faf5f1;
}

.screening-field {
	display: block;
	margin-bottom: 20px;

	span {
		display: block;
		margin-bottom: 6px;
		font-size: 14px;
		font-weight: 600;
		color: #344256;
	}

	input,
	textarea {
		width: 100%;
		border: 1px solid #e1e7ef;
		border-radius: 8px;
		padding: 12px 14px;
		font-size: 16px;
		color: #344256;
		outline: none;
		background: #fff;
	}

	textarea {
		resize: vertical;
	}

	em {
		display: block;
		margin-top: 4px;
		font-style: normal;
		font-size: 12px;
		color: #94a3b8;
	}

	input[readonly] {
		background: #fff;
		color: #344256;
	}
}

.screening-score-link {
	display: block;
	width: 100%;
	margin: -12px 0 20px;
	padding: 0;
	border: 0;
	background: none;
	text-align: right;
	color: #6390c3;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
	cursor: pointer;
}

.screening-bands {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	margin: -8px 0 20px;
	padding: 12px 12px 12px 6px;
	border-radius: 5px;
	background: #eef3f8;
	color: #4477b2;
	font-size: 14px;
	line-height: 20px;

	ul,
	p {
		flex: 1;
		min-width: 0;
	}

	ul {
		margin: 0;
		padding-left: 21px;
		list-style: disc;
	}
}

.screening-bands__close {
	flex-shrink: 0;
	width: 16px;
	height: 16px;
	padding: 0;
	border: 0;
	background: none;
	color: #4477b2;
	cursor: pointer;

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}
}

.screening-docs__input {
	display: none;
}

.screening-docs__hint {
	margin-bottom: 24px;
	color: #65758b;
	font-size: 16px;
	line-height: 24px;
}

.screening-docs__status {
	margin-top: 12px;
	color: #65758b;
	font-size: 14px;
}

.screening-drop {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	padding: 22px 26px 26px;
	border: 2px dashed #d1d5db;
	border-radius: 6px;
	background: #fff;
	cursor: pointer;

	img {
		width: 48px;
		height: 48px;
	}

	span {
		margin-top: 4px;
		color: #4b5563;
		font-size: 14px;
		font-weight: 600;
		line-height: 20px;
	}

	small {
		margin-top: 4px;
		color: #6b7280;
		font-size: 12px;
		line-height: 16px;
		text-align: center;
	}

	&:disabled {
		opacity: 0.6;
		cursor: default;
	}
}

.screening-file-error {
	margin-top: 8px;
	color: #db5a41;
	font-size: 14px;
}

.screening-files {
	display: flex;
	flex-direction: column;
	gap: 24px;

	li {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 12px;
		color: #48566a;
		font-size: 14px;
		line-height: 20px;
	}

	span {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	button {
		flex-shrink: 0;
		width: 16px;
		height: 16px;

		img {
			width: 16px;
			height: 16px;
		}

		&:disabled {
			opacity: 0.5;
		}
	}
}

.screening-upload-link {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	width: 100%;
	margin-top: 16px;
	padding: 12px 0;
	color: #d89210;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;

	img {
		width: 24px;
		height: 24px;
	}

	&:disabled {
		opacity: 0.5;
	}
}

.screening-success {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 16px 32px 16px 24px;
	background: #e8f9f7;

	div {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		min-width: 0;
	}

	p {
		color: #2d8579;
		font-size: 14px;
		font-weight: 600;
		line-height: 20px;
	}

	svg {
		flex-shrink: 0;
	}
}
</style>
